import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { filter, firstValueFrom } from 'rxjs';
import { ChildWindowBuilderService, WindowTarget } from './embedding/child.service';
import { AuthenticationService } from './authentication/authentication.service';
import { SissCloudTokenService } from '../sign/siss-cloud/siss-cloud-sso';
import { ServerRootService } from '../utils/routing/serverroot.service';


const wait = (t: number) => new Promise<number>((resolve, reject) => setTimeout(() => resolve(t), t));

export const extractRegex = (matchRegex: RegExp, replaceRegex: RegExp, text: string): string[] =>
    (text.match(matchRegex) ?? []).map((match: string) => match.replace(replaceRegex, ''));

@Injectable({
    providedIn: 'root',
})
export class NreSissWayService extends ServerRootService {
    test = false;
    accessToken = '';
    pollingToken = '';
    // constructor
    readonly statoElaborazioneRegex = /<statoElaborazione>([\s\S]*?)<\/statoElaborazione>/g;
    readonly statoRisultatoRegex = /<risultato>([\s\S]*?)<\/risultato>/g;

    constructor(
        private httpClient: HttpClient,
        private translate: TranslateService,
        private childWindow: ChildWindowBuilderService,
        private sissCloudTokenService: SissCloudTokenService,
        private auth: AuthenticationService,
    ) {
        super();
    }

    getServerRoot() {
        return this.test ? 'test' : super.getServerRoot();
    }

    protected getHeader(contentType: string): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': contentType,
            Authorization: `Bearer ${this.accessToken}`,
        });
    }

    private async getResultState(): Promise<string> {
        /* dare errore se la risposta non e` una stringa???
         * dato che l'endpoint terze parti non e` affidabile ritornare una stringa vuota se la response non e` string
         * e gestire con un apposito messaggio di errore nel caso di stringa vuota*/
        const res = await firstValueFrom(
            this.httpClient.get<string>(
                `${this.getServerRoot()}/sissway-api/getResultState?token=${this.pollingToken}`,
                {
                    headers: this.getHeader('application/json'),
                    responseType: 'text' as 'json',
                },
            ),
        );

        return res ?? '';
    }

    /**
     *
     * @param idExamination si tratta dell'idExamination selezionato
     * @param serviceUrl
     * @private
     */
    private async getContextServiceData(idExamination = '', serviceUrl = 'services'): Promise<string> {
        const res = await firstValueFrom(
            this.httpClient.post<string>(
                `${this.getServerRoot()}/sissway-api/${serviceUrl} `,
                { idExaminations: [idExamination] },
                {
                    headers: this.getHeader('text/HTML'),
                    responseType: 'text' as 'json',
                },
            ),
        );

        return res ?? '';
    }

    extractStatoElaborazione(text: string): string[] {
        return extractRegex(this.statoElaborazioneRegex, /<statoElaborazione>|<\/statoElaborazione>/g, text);
    }

    extractUrl(text: string): string {
        return extractRegex(/<url>([\s\S]*?)<\/url>/g, /<url>|<\/url>/g, text)[0] ?? '';
    }

    extractToken(text: string): string {
        return extractRegex(/<token>([\s\S]*?)<\/token>/g, /<token>|<\/token>/g, text)[0] ?? '';
    }

    checkElaborazioneResultsState(resultStatusAnswer: string): boolean {
        const values = this.extractStatoElaborazione(resultStatusAnswer);
        return values[0] == '30' && values[1] == '40' && values[2] == '50';
    }

    extractStatoRisultato(text: string): string {
        const matchResult = extractRegex(this.statoRisultatoRegex, /<risultato>|<\/risultato>/g, text);
        return matchResult[0] ?? '';
    }

    async getResultStatePolling(): Promise<string> {
        let response = false;
        while (!response) {
            try {
                const resultStatus = await this.getResultState();
                if (this.checkElaborazioneResultsState(resultStatus)) {
                    response = true;
                    const b64Response = this.extractStatoRisultato(resultStatus);
                    return atob(b64Response);
                } else {
                    await wait(200);
                }
            } catch (err: any) {
                console.error(err);
                throw err;
            }
        }
    }

    async openServiceContextWindow(requestData = '', serviceUrl = 'services'): Promise<boolean> {
        try {
            const userId = this.auth.getUserId();
            this.accessToken = await this.sissCloudTokenService.getAccessToken(userId);
            const xmlResponse = await this.getContextServiceData(requestData, serviceUrl);
            const url = this.extractUrl(xmlResponse);
            const token = this.extractToken(xmlResponse);
            this.pollingToken = token;
            const windowWidth = window.innerWidth < 1280 ? window.innerWidth : 1280;
            const windowHeight = window.innerHeight < 768 ? window.innerHeight : 768;

            if (url && token) {
                const serviceWindow = await this.childWindow
                    .withUrl(url)
                    .withTarget(WindowTarget.Blank)
                    .withSize(windowWidth, windowHeight)
                    .open();

                return await firstValueFrom(serviceWindow.closed$().pipe(filter((closed) => closed)));
            }
        } catch (err: any) {
            console.error(err);
            throw err;
        }
    }


}
